"use client";

import React, { useRef, useState } from "react";
import type { FeatureCollection, Point, Polygon } from "geojson";
import type { GeoJSONSource } from "maplibre-gl";
import Map, {
  Layer,
  LngLatBoundsLike,
  MapRef,
  Marker,
  Popup,
  Source,
} from "react-map-gl/maplibre";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLocationDot } from "@fortawesome/sharp-solid-svg-icons";
import { circleBounds, circlePolygon } from "@/utils/map-circle";
export const mapToken =
  "eyJkcGZ4IjogImNfb2dfYl9zeXN0ZW1lcl93b3JsZCIsICJyZWYiOiAiR3JlZW5sYW5kIiwgInBhciI6ICJjX29nX2Jfc3lzdGVtZXIiLCAicHJpdnMiOiAicmdkamhmVlk3R3RFQTNCWiJ9.UH1hNUReClx6Vp2jtR5z/UJkegSZHH3FFXd7zr7UIz4dJpvKwfI3g74+HUCa0X5590Pt9EfbDlF0MBfD5M6+Hg";

const clusterSourceId = "markers";
const clusterLayerId = "clusters";
const unclusteredLayerId = "unclustered-point";

type MarkerData = {
  longitude: number;
  latitude: number;
  zoom?: number;
  popover?: React.ReactNode;
};
/** An approximate area drawn as a circle of `radius` metres, filled lightly in the pin colour, never as a pin. */
type AreaData = {
  longitude: number;
  latitude: number;
  radius: number;
  popover?: React.ReactNode;
};
const areaSourceId = "areas";

type VisibleMapItem =
  | {
      type: "cluster";
      clusterId: number;
      pointCount: number;
      longitude: number;
      latitude: number;
    }
  | {
      type: "marker";
      markerId: number;
      longitude: number;
      latitude: number;
    };

const MapWrapper: React.FC<{
  markers: MarkerData[];
  areas?: AreaData[];
  /** the closest zoom, also when fitting the markers and areas (14 when absent) */
  maxZoom?: number;
}> = ({ markers, areas = [], maxZoom }) => {
  const [popoverPosition, setPopoverPosition] = useState<"nearPin" | "bottom">(
    "nearPin",
  );
  const [popupInfo, setPopupInfo] = useState<{
    lat: number;
    lng: number;
    popover?: React.ReactNode;
  } | null>(null);
  const mapRef = useRef<MapRef>(null);
  const shouldCluster = markers.length > 1;
  const [visibleMapItems, setVisibleMapItems] = useState<VisibleMapItem[]>([]);
  const [areaColor, setAreaColor] = useState("#1f2937");
  const areaFeatures: FeatureCollection<Polygon> = {
    type: "FeatureCollection",
    features: areas.map((area) => ({
      type: "Feature",
      geometry: circlePolygon(area.latitude, area.longitude, area.radius),
      properties: {},
    })),
  };
  const corners = [
    ...markers.map((m) => [m.longitude, m.latitude]),
    ...areas.flatMap((area) =>
      circleBounds(area.latitude, area.longitude, area.radius),
    ),
  ];
  const areaBounds: LngLatBoundsLike | undefined = areas.length
    ? [
        [
          Math.min(...corners.map((c) => c[0])),
          Math.min(...corners.map((c) => c[1])),
        ],
        [
          Math.max(...corners.map((c) => c[0])),
          Math.max(...corners.map((c) => c[1])),
        ],
      ]
    : undefined;

  const markerFeatures: FeatureCollection<Point, { markerId: number }> = {
    type: "FeatureCollection",
    features: markers.map((marker, index) => ({
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: [marker.longitude, marker.latitude],
      },
      properties: {
        markerId: index,
      },
    })),
  };

  const focusMarker = (marker: MarkerData) => {
    const width = mapRef.current?.getContainer().clientWidth;

    mapRef.current?.flyTo({
      center: [marker.longitude, marker.latitude],
      duration: 1000,
    });

    if (width && width < 768) {
      setPopoverPosition("bottom");
    } else {
      setPopoverPosition("nearPin");
    }

    if (marker.popover) {
      setPopupInfo({
        lat: marker.latitude,
        lng: marker.longitude,
        popover: marker.popover,
      });
      return;
    }

    setPopupInfo(null);
  };

  const fitBounds = () => {
    if (!mapRef.current) return;
    if (areaBounds) {
      const reduced = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;
      mapRef.current.fitBounds(areaBounds, {
        padding: 40,
        duration: reduced ? 0 : 1000,
        maxZoom: maxZoom ?? 14,
      });
      return;
    }
    if (markers.length === 0) return;

    const bounds: LngLatBoundsLike = [
      [
        Math.min(...markers.map((m) => m.longitude)),
        Math.min(...markers.map((m) => m.latitude)),
      ],
      [
        Math.max(...markers.map((m) => m.longitude)),
        Math.max(...markers.map((m) => m.latitude)),
      ],
    ];

    mapRef.current.fitBounds(bounds, {
      padding: 120,
      duration: 1000,
      maxZoom: maxZoom ?? 14,
    });
  };

  const updateVisibleMapItems = () => {
    if (!mapRef.current || !shouldCluster) {
      return;
    }

    const map = mapRef.current.getMap();
    const bounds = map.getBounds();
    const features = map.querySourceFeatures(clusterSourceId);
    const nextVisibleItems: VisibleMapItem[] = [];
    const seenKeys = new Set<string>();

    for (const feature of features) {
      if (feature.geometry.type !== "Point") {
        continue;
      }

      const [longitude, latitude] = feature.geometry.coordinates;

      if (!bounds.contains([longitude, latitude])) {
        continue;
      }

      if (feature.properties?.cluster) {
        const clusterId = Number(feature.properties.cluster_id);
        const pointCount = Number(feature.properties.point_count);
        const clusterKey = `cluster-${clusterId}`;

        if (seenKeys.has(clusterKey)) {
          continue;
        }

        seenKeys.add(clusterKey);
        nextVisibleItems.push({
          type: "cluster",
          clusterId,
          pointCount,
          longitude,
          latitude,
        });
        continue;
      }

      const markerId = Number(feature.properties?.markerId);

      if (!Number.isInteger(markerId) || markerId < 0) {
        continue;
      }

      const markerKey = `marker-${markerId}`;

      if (seenKeys.has(markerKey)) {
        continue;
      }

      seenKeys.add(markerKey);
      nextVisibleItems.push({
        type: "marker",
        markerId,
        longitude,
        latitude,
      });
    }

    nextVisibleItems.sort((left, right) => {
      if (left.type !== right.type) {
        return left.type === "cluster" ? -1 : 1;
      }

      if (left.type === "cluster" && right.type === "cluster") {
        return left.clusterId - right.clusterId;
      }

      if (left.type === "marker" && right.type === "marker") {
        return left.markerId - right.markerId;
      }

      return 0;
    });

    setVisibleMapItems((previousItems) => {
      if (
        previousItems.length === nextVisibleItems.length &&
        previousItems.every((item, index) => {
          const nextItem = nextVisibleItems[index];

          if (item.type !== nextItem.type) {
            return false;
          }

          if (item.type === "cluster" && nextItem.type === "cluster") {
            return item.clusterId === nextItem.clusterId;
          }

          if (item.type === "marker" && nextItem.type === "marker") {
            return item.markerId === nextItem.markerId;
          }

          return false;
        })
      ) {
        return previousItems;
      }

      return nextVisibleItems;
    });
  };

  const focusCluster = (
    clusterId: number,
    longitude: number,
    latitude: number,
  ) => {
    const source = mapRef.current?.getMap().getSource(clusterSourceId) as
      | GeoJSONSource
      | undefined;

    if (!source) {
      return;
    }

    void source
      .getClusterExpansionZoom(clusterId)
      .then((zoom) => {
        mapRef.current?.easeTo({
          center: [longitude, latitude],
          zoom,
          duration: 500,
        });
      })
      .catch(() => {
        return;
      });

    setPopupInfo(null);
  };

  return (
    <Map
      initialViewState={
        areaBounds
          ? {
              bounds: areaBounds,
              fitBoundsOptions: { padding: 40, maxZoom: maxZoom ?? 14 },
            }
          : markers.length === 1
            ? {
                longitude: markers[0].longitude,
                latitude: markers[0].latitude,
                zoom: markers[0].zoom ?? 12,
              }
            : undefined
      }
      {...(maxZoom !== undefined ? { maxZoom } : {})}
      cooperativeGestures={true}
      attributionControl={false}
      mapStyle={`https://c_og_b_systemer_world.tiles.viamap.net/v1/style.json?token=${mapToken}`}
      ref={mapRef}
      onIdle={shouldCluster ? updateVisibleMapItems : undefined}
      onLoad={() => {
        if (areas.length && mapRef.current) {
          const color = getComputedStyle(mapRef.current.getContainer())
            .getPropertyValue("--map-pin-color")
            .trim();
          if (color) setAreaColor(color);
        }
        fitBounds();
        updateVisibleMapItems();
      }}
    >
      {areas.length > 0 && (
        <Source id={areaSourceId} type="geojson" data={areaFeatures}>
          <Layer
            id="area-fill"
            type="fill"
            paint={{ "fill-color": areaColor, "fill-opacity": 0.15 }}
          />
          <Layer
            id="area-line"
            type="line"
            paint={{ "line-color": areaColor, "line-width": 2 }}
          />
        </Source>
      )}
      {areas.map((area, index) =>
        area.popover ? (
          <Marker
            key={`area-${index}`}
            longitude={area.longitude}
            latitude={area.latitude}
            anchor="center"
            onClick={(e) => {
              e.originalEvent.stopPropagation();
              focusMarker(area);
            }}
          >
            <button
              type="button"
              aria-label="Vis boligen i området"
              className="flex size-7 cursor-pointer items-center justify-center rounded-full border-2 border-white bg-[var(--map-pin-color)] shadow-lg"
            >
              <span className="size-2 rounded-full bg-white" />
            </button>
          </Marker>
        ) : null,
      )}
      {shouldCluster ? (
        <>
          <Source
            id={clusterSourceId}
            type="geojson"
            data={markerFeatures}
            cluster={true}
            clusterMaxZoom={15}
            clusterRadius={60}
          >
            <Layer
              id={clusterLayerId}
              type="circle"
              filter={["has", "point_count"]}
              paint={{
                "circle-color": "#e11d48",
                "circle-radius": 1,
                "circle-opacity": 0,
                "circle-stroke-width": 0,
              }}
            />
            <Layer
              id={unclusteredLayerId}
              type="circle"
              filter={["!", ["has", "point_count"]]}
              paint={{
                "circle-color": "#ffffff",
                "circle-radius": 1,
                "circle-stroke-width": 0,
                "circle-opacity": 0,
                "circle-stroke-opacity": 0,
              }}
            />
          </Source>

          {visibleMapItems.map((item) => {
            if (item.type === "cluster") {
              return (
                <Marker
                  key={`cluster-${item.clusterId}`}
                  longitude={item.longitude}
                  latitude={item.latitude}
                  anchor="center"
                  onClick={(e) => {
                    e.originalEvent.stopPropagation();
                    focusCluster(item.clusterId, item.longitude, item.latitude);
                  }}
                >
                  <button
                    className="flex size-12 cursor-pointer items-center justify-center rounded-full border-4 border-white text-[var(--map-pin-color)] text-sm font-semibold shadow-lg"
                    style={{ backgroundColor: "currentColor" }}
                    type="button"
                    aria-label={`Zoom to cluster with ${item.pointCount} markers`}
                  >
                    <span className="text-white">{item.pointCount}</span>
                  </button>
                </Marker>
              );
            }

            const marker = markers[item.markerId];

            if (!marker) {
              return null;
            }

            return (
              <Marker
                key={`marker-${item.markerId}`}
                longitude={item.longitude}
                latitude={item.latitude}
                anchor="bottom"
                onClick={(e) => {
                  e.originalEvent.stopPropagation();
                  focusMarker(marker);
                }}
              >
                <FontAwesomeIcon
                  className="size-10 cursor-pointer text-[var(--map-pin-color)]"
                  icon={faLocationDot}
                />
              </Marker>
            );
          })}
        </>
      ) : (
        markers.map((marker, index) => (
          <Marker
            key={index}
            longitude={marker.longitude}
            latitude={marker.latitude}
            anchor="bottom"
            onClick={(e) => {
              e.originalEvent.stopPropagation();
              focusMarker(marker);
            }}
          >
            <FontAwesomeIcon
              className="size-10 text-[var(--map-pin-color)] cursor-pointer"
              icon={faLocationDot}
            />
          </Marker>
        ))
      )}

      <style jsx global>{`
        .maplibregl-popup-content {
          scale: 0.75;
          transform-origin: left;
        }
      `}</style>

      {popupInfo &&
        (popoverPosition === "nearPin" ? (
          <Popup
            latitude={popupInfo.lat}
            longitude={popupInfo.lng}
            anchor="left"
            onClose={() => setPopupInfo(null)}
            offset={[10, -30]}
            maxWidth="none"
          >
            {popupInfo.popover}
          </Popup>
        ) : (
          <div className="absolute bottom-0 right-0 p-2 scale-75 origin-bottom-right">
            <div className="mx-auto max-w-72">{popupInfo.popover}</div>
          </div>
        ))}
    </Map>
  );
};

export default MapWrapper;
